import { History } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useMilestoneHistory } from "@/hooks/use-milestones";

interface MilestoneHistoryDialogProps {
  projectId: string;
  milestoneId: string;
  milestoneName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatFieldName(field: string | null): string {
  if (!field) return "—";
  return field.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
}

function formatValue(v: string | null): string {
  return v ?? "—";
}

export function MilestoneHistoryDialog({
  projectId,
  milestoneId,
  milestoneName,
  open,
  onOpenChange,
}: MilestoneHistoryDialogProps) {
  const { data, isLoading, isError } = useMilestoneHistory(projectId, open ? milestoneId : "");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-[#334155]">
            <History className="h-4 w-4" />
            {milestoneName} — History
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : isError ? (
            <p className="text-sm text-destructive">Failed to load history.</p>
          ) : !data || data.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No history recorded yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {data.map((entry) => (
                <li key={entry.id} className="py-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-gray-900">
                      {entry.action === "reopen"
                        ? "Milestone reopened"
                        : `${formatFieldName(entry.fieldChanged)} updated`}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatTimestamp(entry.createdAt)}
                    </span>
                  </div>
                  {entry.action !== "reopen" && (
                    <div className="mt-1 text-xs text-gray-600">
                      <span className="line-through text-gray-400">
                        {formatValue(entry.priorValue)}
                      </span>
                      {" → "}
                      <span className="text-gray-900">{formatValue(entry.newValue)}</span>
                    </div>
                  )}
                  {entry.changedByName && (
                    <div className="mt-0.5 text-xs text-gray-500">by {entry.changedByName}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
